/**
 * Installs the self-healing 401 interceptor. Thin wiring only — the decision logic (and its tests)
 * live in src/plugins/staleAuthRecovery.js, which is kept import-free so the node-test suite can
 * execute it without pulling axios/firebase. This file owns the environment: axios registration,
 * the version fetch, the Firebase token refresh, and the existing stale-bundle handler.
 *
 * Mirrors the phiMaskInterceptor.js / phiMask.js split (thin interceptor vs pure, tested core).
 * See staleAuthRecovery.js for the full rationale (the 2026-10-05 tx-presenter stale-bundle 401).
 */

import axios from 'axios';
import { reportStaleBundle } from '@/services/versionCheck';
import { handleAuthError } from './staleAuthRecovery';
import { reportSessionProblem } from '@/services/sessionHealth';

// Same source versionCheck.js uses — baked into the bundle at build time.
const BUILD_VERSION = process.env.VUE_APP_BUILD_VERSION || '';

/** SPA session gate — the same localStorage flag the route guards read. */
function isAuthenticated() {
  try {
    return window.localStorage.getItem('authenticated') === 'true';
  } catch (_e) {
    return false;
  }
}

/** The server's current build version, cache-busted (mirrors versionCheck.checkVersion). */
async function fetchServerVersion() {
  const r = await fetch('/version.json?t=' + Date.now());
  if (!r.ok) return null;
  const d = await r.json();
  return d && d.version;
}

/**
 * Best-effort Firebase token refresh. Firebase is imported lazily (dynamic import inside the
 * function, never at module top) both to keep startup light and so staleAuthRecovery's test loader
 * never touches firebase. If there is no current user, there is nothing to refresh.
 */
async function refreshFirebaseToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (user) await user.getIdToken(true); // force refresh; the NEXT call carries a fresh token
}

/**
 * Register the response interceptor. Fire-and-forget: it NEVER alters or swallows the rejection —
 * the original error is always re-rejected so every caller's existing error path runs unchanged.
 * The self-heal (reload on stale bundle / token refresh) happens asynchronously alongside.
 */
export function installStaleAuthInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        handleAuthError(error, {
          isAuthenticated,
          fetchServerVersion,
          buildVersion: BUILD_VERSION,
          refreshToken: refreshFirebaseToken,
          onStale: reportStaleBundle,
        }).then((outcome) => {
          // Current bundle, so the TOKEN is the problem. A refresh can't help a tab with no
          // Firebase user (the zombie session) — hand it to the session-health guard, which
          // re-checks and heals or prompts. No-op when no guard is mounted.
          if (outcome === 'token-refresh') reportSessionProblem('http-401');
        }).catch(() => {});
      } catch (_e) {
        // An interceptor must never throw — that would replace the real error with ours.
      }
      return Promise.reject(error);
    }
  );
}
